import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Plus, Users, Boxes } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const COLORS = ["#00E5FF", "#FF007F", "#00C853", "#AA00FF", "#FF6D00", "#FFD600", "#2962FF", "#FF3333"];
const EMOJIS = ["🎓", "🚀", "🤖", "⭐", "🎮", "🧪", "🎨", "📚", "⚡", "🌍", "🦖", "🛸"];

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "ahora";
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return `hace ${Math.floor(s / 86400)} d`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", course: "", color: COLORS[0], emoji: EMOJIS[0], cover_image: "" });

  const load = async () => setClasses(await api.listClasses());
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new")) { setOpen(true); setParams({}); } }, [params]);

  const createClass = async () => {
    if (!form.name.trim()) return toast.error("Escribe un nombre");
    const c = await api.createClass(form);
    toast.success("Clase creada 🎉");
    setOpen(false);
    setForm({ name: "", course: "", color: COLORS[0], emoji: EMOJIS[0], cover_image: "" });
    navigate(`/clase/${c.id}`);
  };

  const activeClasses = classes.filter((c) => !c.archived);
  const isAdmin = user?.role === "admin";
  const groupsByTeacher = isAdmin
    ? Object.entries(activeClasses.reduce((acc, c) => { const k = c.owner_name || "Sin profesor"; (acc[k] = acc[k] || []).push(c); return acc; }, {}))
    : [["", activeClasses]];

  const Card = ({ c, i }) => (
    <motion.button key={c.id} data-testid={`class-card-${c.id}`}
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      onClick={() => navigate(`/clase/${c.id}`)}
      className="group text-left bg-card rounded-3xl border-2 border-border overflow-hidden hover:-translate-y-1.5 hover:glow-primary transition-transform">
      <div className="h-24 relative flex items-center justify-center" style={{ background: c.cover_image ? undefined : `linear-gradient(135deg, ${c.color}, ${c.color}99)` }}>
        {c.cover_image && <img src={c.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <span className="relative text-5xl drop-shadow-lg">{c.emoji || "🎓"}</span>
        {c.visible === false && <span data-testid={`hidden-chip-${c.id}`} className="absolute top-2 right-2 text-[10px] font-extrabold px-2 py-1 rounded-full bg-black/60 text-white">OCULTA</span>}
      </div>
      <div className="p-5">
        <div className="font-display font-extrabold text-xl leading-tight">{c.name}</div>
        <div className="text-sm text-muted-foreground">{c.course || "Sin curso"}{isAdmin && c.owner_name ? ` · ${c.owner_name}` : ""}</div>
        {c.description && <div data-testid={`class-desc-${c.id}`} className="text-xs text-muted-foreground/80 mt-0.5 truncate">{c.description}</div>}
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs font-semibold">
          <span className="px-2.5 py-1 rounded-full bg-muted flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.student_count}</span>
          <span className="px-2.5 py-1 rounded-full bg-muted flex items-center gap-1"><Boxes className="w-3.5 h-3.5" /> {c.group_count}</span>
        </div>
      </div>
    </motion.button>
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold">{t("dashTitle")}</h1>
          <p className="text-muted-foreground mt-2 text-base">{t("dashSubtitle")}</p>
        </div>
        <Button data-testid="new-class-btn" onClick={() => setOpen(true)} className="h-12 px-6 rounded-full text-base font-bold gap-2 glow-primary"><Plus className="w-5 h-5" /> {t("newClass")}</Button>
      </div>

      {activeClasses.length === 0 ? (
        <div className="bg-card rounded-3xl border border-dashed border-border p-16 text-center">
          <div className="text-5xl mb-3">🎓</div>
          <p className="text-muted-foreground">{t("noClasses")}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {groupsByTeacher.map(([teacher, list]) => (
            <div key={teacher || "all"} className="space-y-4">
              {teacher && <h2 className="font-display font-extrabold text-lg flex items-center gap-2" data-testid={`teacher-group-${teacher}`}><Users className="w-5 h-5 text-primary" /> {teacher} <span className="text-sm text-muted-foreground font-normal">({list.length})</span></h2>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {list.map((c, i) => <Card key={c.id} c={c} i={i} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("newClass")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nombre</Label><Input data-testid="class-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: 4º Primaria A" className="mt-1 rounded-xl" /></div>
            <div><Label>Curso (opcional)</Label><Input data-testid="class-course-input" value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} placeholder="Ej: 2025/2026" className="mt-1 rounded-xl" /></div>
            <div><Label>Emoji</Label>
              <div className="flex gap-1.5 mt-2 flex-wrap">{EMOJIS.map((e) => (<button key={e} data-testid={`emoji-${e}`} onClick={() => setForm({ ...form, emoji: e })} className="w-10 h-10 rounded-xl text-xl flex items-center justify-center transition-transform hover:scale-110" style={{ background: form.emoji === e ? "hsl(var(--accent))" : "hsl(var(--muted))", outline: form.emoji === e ? "2px solid hsl(var(--primary))" : "none" }}>{e}</button>))}</div></div>
            <div><Label>Color</Label><div className="flex gap-2 mt-2 flex-wrap">{COLORS.map((cc) => <button key={cc} onClick={() => setForm({ ...form, color: cc })} className="w-9 h-9 rounded-full hover:scale-110 transition-transform" style={{ background: cc, outline: form.color === cc ? "3px solid hsl(var(--foreground))" : "none", outlineOffset: 2 }} />)}</div></div>
            <div><Label>Imagen de portada (opcional)</Label><Input data-testid="class-cover-input" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="URL de imagen" className="mt-1 rounded-xl" /></div>
          </div>
          <DialogFooter><Button data-testid="create-class-submit" onClick={createClass} className="rounded-full">Crear y entrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
